import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Phone, Mail, Languages } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Icon, Container, ArrowFwd } from "../shared/Shared";
import { cn } from "../../lib/utils";
import { useLang } from "../../context/LanguageContext";

const OfferingsMega = ({ close, groups }) => {
  const [active, setActive] = useState(0);
  const group = groups[active];
  return (
    <div className="mega-panel" data-testid="offerings-mega-menu">
      <div className="mega-panel-inner grid grid-cols-[280px_1fr]">
        <div className="border-e border-slate-100 bg-slate-50/80 p-3">
          {groups.map((g, i) => (
            <button type="button" key={g.group} onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} className={cn("flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-start text-sm font-semibold transition-colors", active === i ? "bg-white text-brand-teal shadow-sm" : "text-slate-700 hover:bg-white/70")}>
              <Icon name={g.icon} className={cn("h-4 w-4", active === i ? "text-brand-orange" : "text-slate-400")} />
              {g.group}
            </button>
          ))}
        </div>
        <div className="p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">{group.group}</p>
          <div className="mt-4 grid grid-cols-2 gap-1">
            {group.items.map((item) => (
              <Link key={item.slug} to={`/offerings/${item.slug}`} onClick={close} className="group flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-slate-50">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-teal transition-transform group-hover:scale-125" />
                <span>
                  <span className="block text-sm font-semibold text-slate-800 group-hover:text-brand-teal">{item.title}</span>
                  <span className="mt-0.5 line-clamp-1 block text-xs text-slate-500">{item.description}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const IndustriesMega = ({ close, industries }) => (
  <div className="mega-panel w-[720px]" data-testid="industries-mega-menu">
    <div className="mega-panel-inner grid grid-cols-2 gap-1 p-4">
      {industries.map((ind) => (
        <Link key={ind.slug} to={`/industries/${ind.slug}`} onClick={close} className="group flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-slate-50">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal transition-colors group-hover:bg-brand-teal group-hover:text-white"><Icon name={ind.icon} className="h-5 w-5" /></span>
          <span>
            <span className="block text-sm font-semibold text-slate-800 group-hover:text-brand-teal">{ind.title}</span>
            <span className="mt-0.5 line-clamp-2 block text-xs text-slate-500">{ind.summary}</span>
          </span>
        </Link>
      ))}
    </div>
  </div>
);

const Navbar = ({ onQuote }) => {
  const { t, c, toggle, lang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { COMPANY, OFFERING_GROUPS, INDUSTRIES } = c;

  const NAV_LINKS = [
    { label: t.nav.works, to: "/works", id: "works" },
    { label: t.nav.blogs, to: "/blogs", id: "blogs" },
    { label: t.nav.group, to: "/group", id: "group" },
    { label: t.nav.about, to: "/about", id: "about" },
    { label: t.nav.careers, to: "/careers", id: "careers" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const close = () => setOpen(null);
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  const LangButton = ({ className }) => (
    <button type="button" onClick={toggle} data-testid="lang-toggle" className={cn("inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-brand-navy transition-colors hover:border-brand-teal hover:text-brand-teal", className)} aria-label="Switch language">
      <Languages className="h-4 w-4" />
      <span className={lang === "en" ? "font-arabic" : ""}>{t.nav.switchLang}</span>
    </button>
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className={cn("hidden border-b text-xs transition-colors lg:block", solid ? "border-slate-200 bg-brand-navy text-slate-200" : "border-white/10 bg-brand-navy/60 text-slate-200 backdrop-blur")}>
        <Container className="flex h-9 items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={COMPANY.phoneHref} className="flex items-center gap-2 transition-colors hover:text-white" dir="ltr"><Phone className="h-3.5 w-3.5 text-brand-orange" /> {COMPANY.phone}</a>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 transition-colors hover:text-white"><Mail className="h-3.5 w-3.5 text-brand-orange" /> {COMPANY.email}</a>
          </div>
          <div className="flex items-center gap-2 text-slate-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />{t.nav.topbar}</div>
        </Container>
      </div>

      <div className={cn("transition-all duration-300", solid ? "bg-white/95 shadow-[0_8px_30px_-12px_rgba(11,27,43,0.25)] backdrop-blur-md" : "bg-white/80 backdrop-blur-md")}>
        <Container className="flex h-[76px] items-center justify-between gap-6">
          <Link to="/" className="flex shrink-0 items-center" data-testid="nav-logo-link" aria-label="Jonaren home">
            <img src={COMPANY.logo} alt="Jonaren Tech & Trade" className="h-12 w-auto object-contain sm:h-14" />
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" onMouseLeave={close}>
            <div className="relative" onMouseEnter={() => setOpen("offerings")}>
              <button type="button" data-testid="nav-offerings" className={cn("nav-link", open === "offerings" && "text-brand-teal")} onClick={() => setOpen(open === "offerings" ? null : "offerings")}>
                {t.nav.offering} <ChevronDown className={cn("h-4 w-4 transition-transform", open === "offerings" && "rotate-180")} />
              </button>
              {open === "offerings" && <OfferingsMega close={close} groups={OFFERING_GROUPS} />}
            </div>
            <div className="relative" onMouseEnter={() => setOpen("industries")}>
              <button type="button" data-testid="nav-industries" className={cn("nav-link", open === "industries" && "text-brand-teal")} onClick={() => setOpen(open === "industries" ? null : "industries")}>
                {t.nav.industries} <ChevronDown className={cn("h-4 w-4 transition-transform", open === "industries" && "rotate-180")} />
              </button>
              {open === "industries" && <IndustriesMega close={close} industries={INDUSTRIES} />}
            </div>
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} onMouseEnter={close} data-testid={`nav-${l.id}`} className={({ isActive }) => cn("nav-link", isActive && "text-brand-teal after:w-full")}>{l.label}</NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangButton className="hidden md:inline-flex" />
            <div className="hidden sm:block">
              <button type="button" onClick={onQuote} data-testid="nav-quote-button" className="btn-primary whitespace-nowrap bg-brand-orange text-white hover:bg-orange-600">
                {t.nav.quote} <ArrowFwd className="h-4 w-4" />
              </button>
            </div>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-slate-200 text-brand-navy lg:hidden" aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side={lang === "ar" ? "left" : "right"} className="w-[340px] overflow-y-auto p-0 sm:w-[400px]">
                <div className="flex items-center justify-between border-b border-slate-100 p-4 pe-14">
                  <img src={COMPANY.logo} alt="Jonaren" className="h-10 w-auto" />
                  <LangButton />
                </div>
                <div className="p-4">
                  <Accordion type="multiple" className="w-full">
                    <AccordionItem value="offerings">
                      <AccordionTrigger className="text-base font-semibold text-brand-navy">{t.nav.offering}</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4">
                          {OFFERING_GROUPS.map((g) => (
                            <div key={g.group}>
                              <p className="mb-1 text-xs font-bold uppercase tracking-wider text-brand-orange">{g.group}</p>
                              <div className="flex flex-col">{g.items.map((i) => <Link key={i.slug} to={`/offerings/${i.slug}`} className="py-1.5 text-sm text-slate-700 hover:text-brand-teal">{i.title}</Link>)}</div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                    <AccordionItem value="industries">
                      <AccordionTrigger className="text-base font-semibold text-brand-navy">{t.nav.industries}</AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col">{INDUSTRIES.map((i) => <Link key={i.slug} to={`/industries/${i.slug}`} className="py-1.5 text-sm text-slate-700 hover:text-brand-teal">{i.title}</Link>)}</div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                  <div className="mt-2 flex flex-col">
                    {NAV_LINKS.map((l) => <Link key={l.to} to={l.to} className="border-b border-slate-100 py-3.5 text-base font-semibold text-brand-navy">{l.label}</Link>)}
                    <Link to="/contact" className="border-b border-slate-100 py-3.5 text-base font-semibold text-brand-navy">{t.nav.contact}</Link>
                  </div>
                  <button type="button" onClick={() => { setMobileOpen(false); onQuote(); }} className="btn-primary mt-6 w-full bg-brand-orange text-white hover:bg-orange-600">{t.nav.quote} <ArrowFwd className="h-4 w-4" /></button>
                  <div className="mt-6 space-y-2 text-sm text-slate-600">
                    <a href={COMPANY.phoneHref} className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-teal" /><span dir="ltr">{COMPANY.phone}</span></a>
                    <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2"><Mail className="h-4 w-4 text-brand-teal" />{COMPANY.email}</a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </Container>
      </div>
    </header>
  );
};

export default Navbar;
